/**
 * Public bets panel: leg-bet tickets still available on the board per color,
 * and the face-down race-bet piles (winner / loser) shown as stacked card
 * backs with the placers listed.
 */

import { CamelUp, PLAYER_COLOR_HEX } from '@mdev/common'
import type { RacingColor } from '@mdev/common'
import { CU, CU_HEADING } from '../theme'
import { BettingSlip, CamelDot, Panel, PanelTitle, RaceCard } from './pieces'

type CamelUpState = CamelUp.CamelUpState

const RACING: readonly RacingColor[] = CamelUp.RACING_COLORS

interface Props {
  state: CamelUpState
}

export function BetsPanel(props: Props) {
  const { state } = props
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Panel>
        <PanelTitle>Leg-bet tickets</PanelTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {RACING.map((c) => {
            const stack = state.legBetStacks[c] ?? []
            return (
              <div key={c} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CamelDot color={c} />
                  <span style={{ fontFamily: CU_HEADING, textTransform: 'capitalize', color: CU.ink }}>{c}</span>
                </span>
                {stack.length === 0 ? (
                  <span style={{ color: CU.inkSoft, fontSize: 13 }}>gone</span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {stack.map((ticket, i) => (
                      <BettingSlip key={i} color={c} value={ticket.value} compact dim={i !== 0} />
                    ))}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </Panel>

      <Panel>
        <PanelTitle>Race bets</PanelTitle>
        <div style={{ display: 'flex', gap: 16 }}>
          <Pile label="Winner" slots={state.racePiles.winner} players={state.players} />
          <Pile label="Loser" slots={state.racePiles.loser} players={state.players} />
        </div>
      </Panel>
    </div>
  )
}

interface PileProps {
  label: string
  slots: CamelUpState['racePiles']['winner']
  players: CamelUpState['players']
}

function Pile({ label, slots, players }: PileProps) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span
        style={{
          fontFamily: CU_HEADING,
          fontSize: 11,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: CU.inkSoft,
        }}
      >
        {label} · {slots.length}
      </span>
      {/* Stacked card backs */}
      <div style={{ position: 'relative', height: 52 }}>
        {slots.length === 0 ? (
          <span style={{ color: CU.inkSoft, fontSize: 13 }}>none yet</span>
        ) : (
          slots.slice(0, 6).map((s, i) => {
            const revealed = s.color !== null
            return (
              <div key={i} style={{ position: 'absolute', left: i * 12, top: 0, zIndex: i }}>
                <RaceCard color={revealed ? (s.color as RacingColor) : undefined} size="sm" />
              </div>
            )
          })
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {slots.map((s, i) => {
          const p = players[s.uid]
          return (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: CU.ink }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: p ? PLAYER_COLOR_HEX[p.color] : '#888',
                  display: 'inline-block',
                }}
              />
              {p?.displayName ?? s.uid}
            </span>
          )
        })}
      </div>
    </div>
  )
}
